import { Fix64 } from "../maths/fix64";
import { RARITY_IDS, Rarity } from "./balance";
import { NO_SUPPLIES, SuppliesData, parseSupplies } from "./supplies";
import { BoostsData, NO_BOOSTS, parseBoosts } from "./boosts";
import { PetRoster, caughtUnlock, petRoster, skinPet } from "./petSkins";

/**
 * The offline economy's data (ADR 0013; DESIGN §8.1, §9.5, §10): gem sources, the Shop, the NPC market, Fortress
 * Tribute, the upgrade queue and the wallet ledger. Parsed from the generated bundle's `chests.gems`, `meta`, `shop`
 * and `market` sections; the rules that read it live in meta/{wallet,shop,market,tribute,upgradeQueue}.ts.
 */

/** Where free gems come from (D20): never from battle chests. */
export interface GemSourcesData {
  /** Per chapter star chest milestone (index 0..2): gems paid with it. */
  readonly starChests: readonly number[];
  /** Boss enemy id → gems for the first clear of the first level that boss guards. */
  readonly bossFirstClear: ReadonlyMap<string, number>;
}

export interface SpeedUpPackData { readonly id: string; readonly minutes: number; readonly gems: number }
/** `chests` average Win chests of resources at the player's level (orders.ts chestShare); at most `perDay` a UTC day. */
export interface ResourcePackData {
  readonly id: string; readonly chests: Fix64; readonly gems: number; readonly perDay: number;
  /** Opens once this level is cleared (0: always; MP-07 War Coffers: 10). */
  readonly unlockAfterLevel: number;
  /** The tile's title ("War Coffers"); null: the plain pack, titled by its contents. */
  readonly name: string | null;
  /** The tile's painted icon id (`hq/icons/res_<icon>.png`; "gold" for the plain packs, "gold_coffers" for War Coffers). */
  readonly icon: string;
  /** Claims a day for nothing (owner 2026-10-21: the free daily gold pack replaced the free Supply Crate); 0: none. */
  readonly freePerDay: number;
}
/**
 * FINISH NOW on a running upgrade (MP-07 (a), shop.json `finishNow`): ceil(time left / stepMinutes) steps, at least
 * `minSteps`, × `gemsPerStep`. `gemsPerStep` 0 (an older bundle): never offered.
 */
export interface FinishNowData { readonly gemsPerStep: number; readonly stepMinutes: number; readonly minSteps: number }
/** A real-money gem pack (the Store port's product): `referencePrice` is only what the sandbox store shows. */
export interface GemPackData {
  readonly id: string; readonly name: string; readonly gems: number; readonly referencePrice: string;
  /** Owner 2026-10-21: a ribbon on the pack ("popular": Most popular, "best": Best value). */
  readonly tag?: "popular" | "best";
}

export interface CratesData {
  readonly slotsPerDay: number;
  /** Per rarity (index = Rarity): the weight a crate rolls it with; 0 = never. */
  readonly weights: readonly number[];
  /** Per rarity: the crate's price in gems (0 where the weight is 0). */
  readonly gems: readonly number[];
  /** MP-08: free crates a UTC day (0 or 1), of `freeRarity`, apart from `slotsPerDay`. */
  readonly freePerDay: number;
  readonly freeRarity: Rarity;
}

/**
 * What a cosmetic dresses (FINISH_PLAN WP8, DESIGN §12 Skins). Skins are data only: nothing in the sim reads them, the
 * rules (unlocks, equip, buy) are @highmarch/app `cosmetics.ts` and the looks are the web's `render/skins/`. MP-23 (the Trophy
 * Hall): `frame` is a title frame round the Commander's name plate, `kill` an effect on enemy deaths (render only).
 */
export const SKIN_KINDS = ["tower", "fortress", "troop", "emblem", "road", "frame", "kill", "pet"] as const;
export type SkinKind = (typeof SKIN_KINDS)[number];

/**
 * How a skin is had: at a level milestone (a tower skin at that tower's own level, any other at the Fortress level),
 * for gems or Marks in the Shop, from the Black Market (gems, on the days it features the skin), as an event
 * reward (the emblem id the event's track pays, data/events.ts), or as a season skin (the War Campaign season, 1-based,
 * whose Premium cosmetic tier pays it: seasons.json; never sold). Prices are resolved from the bands at load. A pet
 * skin (Pets v2) is had only by catching its pet wearing it in battle (`caught`: app pets.ts grants it into `owned`).
 */
export type SkinUnlock =
  | { readonly by: "level"; readonly level: number }
  | { readonly by: "gems" | "marks" | "blackMarket"; readonly band: string; readonly price: number }
  | { readonly by: "event"; readonly emblem: string }
  | { readonly by: "season"; readonly season: number }
  /** M40 (docs/RARES.md): the Starfall set, had only by slaying the Starfall Wyrm (app wyrm.ts grants one the player lacks). */
  | { readonly by: "wyrm" }
  /** MP-23: bought for Trophies (the earned currency) in the Trophy Hall alone; never sold for gems or Marks, never in a pack. */
  | { readonly by: "trophies"; readonly price: number }
  /** Pets v2 (docs/PETS.md): had only by catching the skin's pet wearing it (sim/wildPet.ts; app pets.ts grants it). */
  | { readonly by: "caught" };

export interface SkinData {
  readonly id: string;
  readonly kind: SkinKind;
  readonly unlock: SkinUnlock;
  /** A tower skin only that tower type may wear (M40: the Starfall set, one per type); absent = any tower. */
  readonly tower?: string;
  /** The pet a pet skin fits (pets.json id): required for kind "pet", refused on every other kind. */
  readonly pet?: string;
  /**
   * Owner 2026-10-21: every skin but the Starfall Wyrm's can also be bought for gems in the Shop, at this price (from
   * `skinPriceBands` by the skin's `buyGems` band); its own unlock stays the free route. Absent on a skin already sold
   * for gems (`unlock.price` is its price) and on the Wyrm's.
   */
  readonly buyGems?: number;
  /** The `skinPriceBands` band `buyGems` came from (the Shop frames the skin in its colour). */
  readonly buyGemsBand?: string;
}

export interface SkinsData {
  readonly list: readonly SkinData[];
  /** The level milestones a skin may unlock at (DESIGN §6.3, rescaled for the 60-level campaign). */
  readonly levelMilestones: readonly number[];
  /** Percent of UTC days the Black Market also features one exclusive skin the player lacks. */
  readonly blackMarketPercent: number;
}

export interface ShopData {
  readonly speedUpPacks: readonly SpeedUpPackData[];
  readonly resourcePacks: readonly ResourcePackData[];
  readonly crates: CratesData;
  /** Skin tier id → price in gems (WP8's cosmetics). */
  readonly skinPriceBands: ReadonlyMap<string, number>;
  /** The cosmetics catalogue (WP8): unlocks and prices; empty in an older bundle. */
  readonly skins: SkinsData;
  readonly gemPacks: readonly GemPackData[];
  /** Owner 2026-10-21: the extra gems (percent of the pack) a gem pack's first purchase pays; 0 for none. */
  readonly firstPurchaseBonusPercent: number;
  readonly postLossQuietMinutes: number;
  readonly finishNow: FinishNowData;
  /** Pre-battle supplies (MP-06, data/supplies.ts); none in an older bundle. */
  readonly supplies: SuppliesData;
  /** Battle boosts (owner 2026-10-21, data/boosts.ts); none in an older bundle. */
  readonly boosts: BoostsData;
}

export interface MarketData {
  readonly minSellRarity: Rarity;
  readonly sellFraction: Fix64;
  readonly stockSize: number;
  /** Per rarity: weight in the Trading Post's daily stock. */
  readonly stockWeights: readonly number[];
  readonly buyMarkup: Fix64;
  readonly blackMarketMythicPercent: number;
  /** Per rarity: the Black Market's price in gems (only Legendary and Mythic are ever featured). */
  readonly blackMarketGems: readonly number[];
}

export interface TributeData {
  readonly unlockAfterLevel: number;
  readonly capSeconds: number;
  /** Average Win chests of resources a full Tribute holds. */
  readonly chestsWhenFull: Fix64;
}

export interface CommerceData {
  readonly gems: GemSourcesData;
  readonly shop: ShopData;
  readonly market: MarketData;
  readonly tribute: TributeData;
  /** Queued upgrades each builder can hold (D4). */
  readonly queuePerBuilder: number;
  /** Wallet ledger entries kept (the newest). */
  readonly ledgerSize: number;
}

const SECONDS_PER_HOUR = 3600;
const NO_SKINS: SkinsData = { list: [], levelMilestones: [], blackMarketPercent: 0 };
/** A skin's unlock keys in shop.json, one per skin. */
const UNLOCK_KEYS = ["level", "gems", "marks", "blackMarket", "event", "season", "wyrm", "trophies", "caught"] as const;
const DEFAULT_LEDGER_SIZE = 50;
const NO_FINISH_NOW: FinishNowData = { gemsPerStep: 0, stepMinutes: 60, minSteps: 1 };

/** No commerce data (an old bundle, a test fixture): nothing to buy, no gem sources, Tribute and the queue off. */
export function emptyCommerce(): CommerceData {
  const none = RARITY_IDS.map(() => 0);
  return {
    gems: { starChests: [], bossFirstClear: new Map() },
    shop: { speedUpPacks: [], resourcePacks: [], crates: { slotsPerDay: 0, weights: none, gems: none, freePerDay: 0, freeRarity: Rarity.Rare }, skinPriceBands: new Map(), skins: NO_SKINS, gemPacks: [], firstPurchaseBonusPercent: 0, postLossQuietMinutes: 0, finishNow: NO_FINISH_NOW, supplies: NO_SUPPLIES, boosts: NO_BOOSTS },
    market: { minSellRarity: Rarity.Mythic, sellFraction: Fix64.Zero, stockSize: 0, stockWeights: none, buyMarkup: Fix64.One, blackMarketMythicPercent: 0, blackMarketGems: none },
    tribute: { unlockAfterLevel: Number.MAX_SAFE_INTEGER, capSeconds: 0, chestsWhenFull: Fix64.Zero },
    queuePerBuilder: 0,
    ledgerSize: DEFAULT_LEDGER_SIZE,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

function int(v: Json): number {
  if (typeof v === "number" && Number.isInteger(v)) return v;
  throw new TypeError(`expected integer, got ${JSON.stringify(v)}`);
}
function str(v: Json): string {
  if (typeof v !== "string") throw new TypeError(`expected string, got ${JSON.stringify(v)}`);
  return v;
}
function fix(v: Json): Fix64 { return Fix64.parse(String(v)); }
function req(o: Json, key: string): Json {
  if (o === null || typeof o !== "object" || !(key in o)) throw new Error(`Missing JSON member '${key}'`);
  return o[key];
}
function rarity(s: string): Rarity {
  const i = (RARITY_IDS as readonly string[]).indexOf(s);
  if (i < 0) throw new Error(`unknown rarity '${s}'`);
  return i as Rarity;
}
/** shop.json `crates`: the day's slots, their weights and prices, and the free crate (MP-08; absent = none). */
function parseCrates(c: Json): CratesData {
  return {
    slotsPerDay: int(req(c, "slotsPerDay")), weights: byRarity(req(c, "weights")), gems: byRarity(req(c, "gems")),
    freePerDay: c.freePerDay === undefined ? 0 : int(c.freePerDay), freeRarity: c.freeRarity === undefined ? Rarity.Rare : rarity(str(c.freeRarity)),
  };
}
/** A `{ rarity: n }` object as a per-rarity array (0 where absent). */
function byRarity(o: Json): number[] { return RARITY_IDS.map((r) => (o !== null && typeof o === "object" && r in o ? int(o[r]) : 0)); }
function entries(o: Json): [string, Json][] { return Object.entries(o as Record<string, Json>).filter(([k]) => !k.startsWith("_")); }

/**
 * shop.json's skin catalogue: each skin names exactly one unlock key; a level is one of `skinLevelMilestones`, a band
 * prices from `skinPriceBands` (gems, the Black Market) or `skinMarksBands` (Marks), `trophies` is its own whole price in
 * Trophies (MP-23, the Trophy Hall). Throws on anything else.
 */
function parseSkins(s: Json, towerIds: readonly string[], pets: PetRoster): SkinsData {
  const milestones = (req(s, "skinLevelMilestones") as Json[]).map(int);
  const gems = new Map(entries(req(s, "skinPriceBands")).map(([k, v]) => [k, int(v)]));
  const marks = new Map(entries(req(s, "skinMarksBands")).map(([k, v]) => [k, int(v)]));
  const band = (bands: Map<string, number>, b: string, id: string): number => {
    const price = bands.get(b);
    if (price === undefined || price <= 0) throw new Error(`skin '${id}': no price band '${b}'`);
    return price;
  };
  const seen = new Set<string>();
  const list = (req(s, "skins") as Json[]).map((k): SkinData => {
    const id = str(req(k, "id")), kind = str(req(k, "kind")) as SkinKind;
    if (!SKIN_KINDS.includes(kind)) throw new Error(`skin '${id}': unknown kind '${kind}'`);
    if (seen.has(id)) throw new Error(`duplicate skin '${id}'`);
    seen.add(id);
    const keys = UNLOCK_KEYS.filter((u) => u in k);
    if (keys.length !== 1) throw new Error(`skin '${id}' needs exactly one of ${UNLOCK_KEYS.join("/")}`);
    const by = keys[0];
    let unlock: SkinUnlock;
    if (by === "level") {
      const level = int(k.level);
      if (!milestones.includes(level)) throw new Error(`skin '${id}': level ${level} is not a skin milestone`);
      unlock = { by, level };
    } else if (by === "event") unlock = { by, emblem: str(k.event) };
    else if (by === "season") {
      const season = int(k.season);
      if (season < 1) throw new Error(`skin '${id}': season ${season} must be 1 or later`);
      unlock = { by, season };
    }
    else if (by === "wyrm") {
      if (k.wyrm !== true) throw new Error(`skin '${id}': wyrm must be true`);
      unlock = { by };
    }
    else if (by === "trophies") {
      const price = int(k.trophies);
      if (price <= 0) throw new Error(`skin '${id}': trophies must be a price above 0`);
      unlock = { by, price };
    }
    else if (by === "caught") unlock = caughtUnlock(id, kind, k);
    else { const b = str(k[by]); unlock = { by, band: b, price: band(by === "marks" ? marks : gems, b, id) }; }
    let extra: { buyGems?: number; buyGemsBand?: string } = {};
    if ("buyGems" in k) {
      if (unlock.by === "gems" || unlock.by === "wyrm") throw new Error(`skin '${id}': buyGems is for a skin not already sold for gems, never the Wyrm's`);
      extra = { buyGems: band(gems, str(k.buyGems), id), buyGemsBand: str(k.buyGems) };
    }
    const pet = skinPet(id, kind, k, pets);
    if (pet !== undefined) return { id, kind, unlock, pet, ...extra };
    if (!("tower" in k)) return { id, kind, unlock, ...extra };
    const tower = str(k.tower);
    if (kind !== "tower" || !towerIds.includes(tower)) throw new Error(`skin '${id}': tower '${tower}' needs a tower skin and a tower type`);
    return { id, kind, unlock, tower, ...extra };
  });
  const pct = int(req(s, "skinBlackMarketPercent"));
  if (pct < 0 || pct > 100) throw new Error("skinBlackMarketPercent must be 0..100");
  return { list, levelMilestones: milestones, blackMarketPercent: pct };
}

function parseFinishNow(f: Json): FinishNowData {
  const out = { gemsPerStep: int(req(f, "gemsPerStep")), stepMinutes: int(req(f, "stepMinutes")), minSteps: int(req(f, "minSteps")) };
  if (out.stepMinutes < 1 || out.minSteps < 1) throw new Error("shop.finishNow: stepMinutes and minSteps at least 1");
  return out;
}

/** Reads the commerce data from the bundle root; sections an older bundle lacks fall back to `emptyCommerce`. */
export function parseCommerce(root: Json): CommerceData {
  const empty = emptyCommerce();
  const meta = req(root, "meta");
  const g = req(root, "chests")?.gems;
  const gems: GemSourcesData = g === undefined ? empty.gems : {
    starChests: (req(g, "starChests") as Json[]).map(int),
    bossFirstClear: new Map(entries(req(g, "bossFirstClear")).map(([k, v]) => [k, int(v)])),
  };
  const s = root.shop;
  const shop: ShopData = s === undefined ? empty.shop : {
    speedUpPacks: (req(s, "speedUpPacks") as Json[]).map((p) => ({ id: str(req(p, "id")), minutes: int(req(p, "minutes")), gems: int(req(p, "gems")) })),
    resourcePacks: (req(s, "resourcePacks") as Json[]).map((p) => ({
      id: str(req(p, "id")), chests: fix(req(p, "chests")), gems: int(req(p, "gems")), perDay: int(req(p, "perDay")),
      unlockAfterLevel: p.unlockAfterLevel === undefined ? 0 : int(p.unlockAfterLevel), name: p.name === undefined ? null : str(p.name), icon: p.icon === undefined ? "gold" : str(p.icon),
      freePerDay: p.freePerDay === undefined ? 0 : int(p.freePerDay),
    })),
    crates: parseCrates(req(s, "crates")),
    skinPriceBands: new Map(entries(req(s, "skinPriceBands")).map(([k, v]) => [k, int(v)])),
    skins: s.skins === undefined ? NO_SKINS : parseSkins(s, ((root.towers?.towers ?? []) as Json[]).map((t) => String(t.id)), petRoster(root)),
    gemPacks: (req(s, "gemPacks") as Json[]).map((p): GemPackData => {
      const base = { id: str(req(p, "id")), name: str(req(p, "name")), gems: int(req(p, "gems")), referencePrice: str(req(p, "referencePrice")) };
      if (p.tag === undefined) return base;
      const tag = str(p.tag);
      if (tag !== "popular" && tag !== "best") throw new Error(`gem pack '${base.id}': tag must be popular or best`);
      return { ...base, tag };
    }),
    firstPurchaseBonusPercent: s.firstPurchaseBonusPercent === undefined ? 0 : int(s.firstPurchaseBonusPercent),
    postLossQuietMinutes: int(req(s, "postLossQuietMinutes")),
    finishNow: s.finishNow === undefined ? NO_FINISH_NOW : parseFinishNow(s.finishNow),
    supplies: parseSupplies(s.supplies),
    boosts: parseBoosts(s.boosts),
  };
  const m = root.market;
  const market: MarketData = m === undefined ? empty.market : {
    minSellRarity: rarity(str(req(m, "minSellRarity"))), sellFraction: fix(req(m, "sellFraction")),
    stockSize: int(req(req(m, "stock"), "size")), stockWeights: byRarity(req(req(m, "stock"), "weights")), buyMarkup: fix(req(req(m, "stock"), "buyMarkup")),
    blackMarketMythicPercent: int(req(req(m, "blackMarket"), "mythicPercent")), blackMarketGems: byRarity(req(req(m, "blackMarket"), "gems")),
  };
  const t = meta.tribute;
  const tribute: TributeData = t === undefined ? empty.tribute : {
    unlockAfterLevel: int(req(t, "unlockAfterLevel")), capSeconds: int(req(t, "capHours")) * SECONDS_PER_HOUR, chestsWhenFull: fix(req(t, "chestsWhenFull")),
  };
  return {
    gems, shop, market, tribute,
    queuePerBuilder: meta.queuePerBuilder === undefined ? empty.queuePerBuilder : int(meta.queuePerBuilder),
    ledgerSize: meta.walletLedgerSize === undefined ? empty.ledgerSize : int(meta.walletLedgerSize),
  };
}
